import React, { useEffect, useState } from 'react';
import { Gift, Clock, Calendar, ChevronRight, Lock, Unlock, Diamond, Crown, Sparkles, Image as ImageIcon, Play, History, X, Plus, Shield, Star } from 'lucide-react';
import { LootboxOpening } from './LootboxOpening';
import { LootboxReward } from './RitualContext';
interface LootboxDashboardProps {
  userTier: 'free' | 'silver' | 'gold' | 'platinum';
  onUpgrade?: () => void;
  onClose?: () => void;
}
type Lootbox = {
  id: string;
  name: string;
  description: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  available: boolean;
  cooldownHours: number;
  lastOpened?: Date;
  requiresPremium: boolean;
  minTier: 'free' | 'silver' | 'gold' | 'platinum';
  potentialRewards: string[];
  imageUrl: string;
};
export const LootboxDashboard: React.FC<LootboxDashboardProps> = ({
  userTier = 'free',
  onUpgrade,
  onClose
}) => {
  const [lootboxes, setLootboxes] = useState<Lootbox[]>([{
    id: 'weekly-premium',
    name: 'Premium Weekly Lootbox',
    description: 'High-value rewards refresh every week',
    rarity: 'epic',
    available: userTier !== 'free',
    cooldownHours: 168,
    requiresPremium: true,
    minTier: 'silver',
    potentialRewards: ['Up to 500 LuvTokens', 'Exclusive Angel Skins', 'Premium Video Content', 'VIP Days Extension'],
    imageUrl: 'https://assets.codepen.io/13471/sparkles.gif'
  }, {
    id: 'daily-reward',
    name: 'Daily Reward Chest',
    description: 'Daily rewards for active members',
    rarity: 'common',
    available: true,
    cooldownHours: 24,
    requiresPremium: false,
    minTier: 'free',
    potentialRewards: ['Up to 50 LuvTokens', 'Chat Time Bonuses', 'XP Boosts'],
    imageUrl: 'https://assets.codepen.io/13471/sparkles.gif'
  }, {
    id: 'platinum-mystery',
    name: 'Platinum Mystery Box',
    description: 'Exclusive high-tier rewards for Platinum members',
    rarity: 'legendary',
    available: userTier === 'platinum',
    cooldownHours: 72,
    requiresPremium: true,
    minTier: 'platinum',
    potentialRewards: ['Up to 1000 LuvTokens', 'Legendary Angel Skins', 'Exclusive Video Content', 'Special Angel Interactions'],
    imageUrl: 'https://assets.codepen.io/13471/sparkles.gif'
  }, {
    id: 'ritual-completion',
    name: 'Ritual Completion Box',
    description: 'Earned by completing daily rituals',
    rarity: 'rare',
    available: false,
    cooldownHours: 0,
    requiresPremium: false,
    minTier: 'free',
    potentialRewards: ['Up to 200 LuvTokens', 'Rare Angel Content', 'Temporary Boosts'],
    imageUrl: 'https://assets.codepen.io/13471/sparkles.gif'
  }]);
  const [selectedLootbox, setSelectedLootbox] = useState<Lootbox | null>(null);
  const [showLootboxOpening, setShowLootboxOpening] = useState(false);
  const [currentReward, setCurrentReward] = useState<LootboxReward | null>(null);
  const [showRewardHistory, setShowRewardHistory] = useState(false);
  const [countdowns, setCountdowns] = useState<Record<string, string>>({});
  // Simulated reward history
  const [rewardHistory] = useState<Array<LootboxReward & {
    date: Date;
    lootboxId: string;
  }>>([{
    id: 'reward-1',
    title: 'Token Bundle',
    description: 'A bundle of LuvTokens to spend on your favorite Angels',
    type: 'tokens',
    amount: 250,
    rarity: 'rare',
    date: new Date(Date.now() - 86400000 * 2),
    lootboxId: 'weekly-premium'
  }, {
    id: 'reward-2',
    title: 'Lingerie Skin: Zoey',
    description: 'Exclusive lingerie skin for Angel Zoey',
    type: 'skin',
    rarity: 'epic',
    imageUrl: 'https://placehold.co/400x300/9333ea/ffffff?text=Zoey+Skin',
    date: new Date(Date.now() - 86400000 * 5),
    lootboxId: 'platinum-mystery'
  }, {
    id: 'reward-3',
    title: 'Chat Time Boost',
    description: 'Extra chat time with your favorite Angels',
    type: 'chatTime',
    amount: 60,
    rarity: 'common',
    date: new Date(Date.now() - 86400000 * 1),
    lootboxId: 'daily-reward'
  }]);
  // Update countdowns every second
  useEffect(() => {
    const updateCountdowns = () => {
      const now = new Date();
      const newCountdowns: Record<string, string> = {};
      lootboxes.forEach(lootbox => {
        if (lootbox.lastOpened && lootbox.cooldownHours > 0) {
          const nextAvailable = new Date(lootbox.lastOpened);
          nextAvailable.setHours(nextAvailable.getHours() + lootbox.cooldownHours);
          if (nextAvailable > now) {
            const diffMs = nextAvailable.getTime() - now.getTime();
            const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMins = Math.floor(diffMs % (1000 * 60 * 60) / (1000 * 60));
            const diffSecs = Math.floor(diffMs % (1000 * 60) / 1000);
            newCountdowns[lootbox.id] = `${diffHrs.toString().padStart(2, '0')}:${diffMins.toString().padStart(2, '0')}:${diffSecs.toString().padStart(2, '0')}`;
          } else {
            // Update lootbox availability if cooldown has expired
            setLootboxes(prev => prev.map(lb => lb.id === lootbox.id ? {
              ...lb,
              available: (lb.minTier === 'free' || lb.minTier === 'silver' && userTier !== 'free' || lb.minTier === 'gold' && (userTier === 'gold' || userTier === 'platinum') || lb.minTier === 'platinum' && userTier === 'platinum') && !lb.requiresPremium
            } : lb));
          }
        }
      });
      setCountdowns(newCountdowns);
    };
    // Initialize with random "last opened" dates for demo purposes
    if (!lootboxes.some(lb => lb.lastOpened) && process.env.NODE_ENV !== 'production') {
      setLootboxes(prev => prev.map(lb => {
        // Random time in the past, between 0 and cooldownHours
        const randomHoursAgo = Math.floor(Math.random() * lb.cooldownHours);
        const lastOpened = new Date();
        lastOpened.setHours(lastOpened.getHours() - randomHoursAgo);
        return {
          ...lb,
          lastOpened: lb.id === 'weekly-premium' ? undefined : lastOpened
        };
      }));
    }
    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [lootboxes, userTier]);
  const handleOpenLootbox = (lootbox: Lootbox) => {
    setSelectedLootbox(lootbox);
    // Generate a random reward based on lootbox rarity
    const reward: LootboxReward = generateRandomReward(lootbox.rarity);
    setCurrentReward(reward);
    setShowLootboxOpening(true);
    // Update lootbox cooldown
    setLootboxes(prev => prev.map(lb => lb.id === lootbox.id ? {
      ...lb,
      available: false,
      lastOpened: new Date()
    } : lb));
  };
  const handleCloseLootboxOpening = () => {
    setShowLootboxOpening(false);
    setSelectedLootbox(null);
    setCurrentReward(null);
  };
  // Helper function to generate random rewards based on rarity
  const generateRandomReward = (rarity: 'common' | 'rare' | 'epic' | 'legendary'): LootboxReward => {
    // Determine reward type based on rarity
    const rewardTypes: Array<LootboxReward['type']> = ['tokens', 'gems', 'vip', 'chatTime'];
    if (rarity === 'rare' || rarity === 'epic' || rarity === 'legendary') {
      rewardTypes.push('skin', 'picture');
    }
    if (rarity === 'epic' || rarity === 'legendary') {
      rewardTypes.push('video');
    }
    const type = rewardTypes[Math.floor(Math.random() * rewardTypes.length)] as LootboxReward['type'];
    // Determine reward amount based on type and rarity
    let amount = 0;
    let title = '';
    let description = '';
    let imageUrl = undefined;
    switch (type) {
      case 'tokens':
        amount = rarity === 'common' ? 25 + Math.floor(Math.random() * 50) : rarity === 'rare' ? 100 + Math.floor(Math.random() * 100) : rarity === 'epic' ? 200 + Math.floor(Math.random() * 300) : 500 + Math.floor(Math.random() * 500);
        title = `${amount} LuvTokens`;
        description = `A bundle of ${amount} LuvTokens to spend on your favorite Angels`;
        break;
      case 'gems':
        amount = rarity === 'common' ? 5 + Math.floor(Math.random() * 10) : rarity === 'rare' ? 15 + Math.floor(Math.random() * 15) : rarity === 'epic' ? 30 + Math.floor(Math.random() * 20) : 50 + Math.floor(Math.random() * 50);
        title = `${amount} DesireGems`;
        description = `${amount} premium DesireGems for exclusive content`;
        break;
      case 'vip':
        amount = rarity === 'rare' ? 1 : rarity === 'epic' ? 3 : rarity === 'legendary' ? 7 : 1;
        title = `${amount} ${amount > 1 ? 'Days' : 'Day'} VIP Extension`;
        description = `Extend your VIP membership by ${amount} ${amount > 1 ? 'days' : 'day'}`;
        break;
      case 'chatTime':
        amount = rarity === 'common' ? 15 : rarity === 'rare' ? 30 : rarity === 'epic' ? 60 : 120;
        title = `${amount} Minutes Chat Boost`;
        description = `${amount} minutes of additional chat time with Angels`;
        break;
      case 'skin':
        const angels = ['Sophia', 'Harper', 'Zoey', 'Lana', 'Mia Khalifa'];
        const angel = angels[Math.floor(Math.random() * angels.length)];
        const skinTypes = ['Lingerie', 'Bikini', 'Cosplay', 'Fantasy'];
        const skinType = skinTypes[Math.floor(Math.random() * skinTypes.length)];
        title = `${skinType} Skin: ${angel}`;
        description = `Exclusive ${skinType.toLowerCase()} skin for Angel ${angel}`;
        imageUrl = `https://placehold.co/400x300/9333ea/ffffff?text=${angel}+${skinType}`;
        break;
      case 'picture':
        const themes = ['Beach Day', 'Bedroom', 'Studio', 'Outdoor'];
        const theme = themes[Math.floor(Math.random() * themes.length)];
        title = `Premium Picture Set: ${theme}`;
        description = `Exclusive ${theme} themed pictures`;
        imageUrl = `https://placehold.co/400x300/3b82f6/ffffff?text=Picture+Set`;
        break;
      case 'video':
        const videoThemes = ['Private Dance', 'Intimate Moments', 'Fantasy Roleplay'];
        const videoTheme = videoThemes[Math.floor(Math.random() * videoThemes.length)];
        title = `Premium Video: ${videoTheme}`;
        description = `Exclusive ${videoTheme} video content`;
        imageUrl = `https://placehold.co/400x300/ef4444/ffffff?text=Premium+Video`;
        break;
    }
    return {
      id: `reward-${Date.now()}`,
      title,
      description,
      type,
      amount,
      rarity,
      ...(imageUrl && {
        imageUrl
      })
    };
  };
  // Format date for display
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  // Get background color based on rarity
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'from-gray-600 to-gray-700';
      case 'rare':
        return 'from-blue-600 to-blue-700';
      case 'epic':
        return 'from-purple-600 to-purple-700';
      case 'legendary':
        return 'from-amber-500 to-amber-600';
      default:
        return 'from-gray-600 to-gray-700';
    }
  };
  // Get border color based on rarity
  const getRarityBorder = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'border-gray-400';
      case 'rare':
        return 'border-blue-400';
      case 'epic':
        return 'border-purple-400';
      case 'legendary':
        return 'border-amber-400';
      default:
        return 'border-gray-400';
    }
  };
  // Get text color based on rarity
  const getRarityText = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'text-gray-300';
      case 'rare':
        return 'text-blue-300';
      case 'epic':
        return 'text-purple-300';
      case 'legendary':
        return 'text-amber-300';
      default:
        return 'text-gray-300';
    }
  };
  // Get icon based on reward type
  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'tokens':
        return <span className="text-xl" data-id="element-17256">💗</span>;
      case 'gems':
        return <Diamond size={20} className="text-blue-400" data-id="element-17257" />;
      case 'vip':
        return <Crown size={20} className="text-amber-400" data-id="element-17258" />;
      case 'skin':
        return <Sparkles size={20} className="text-pink-400" data-id="element-17259" />;
      case 'chatTime':
        return <Clock size={20} className="text-green-400" data-id="element-17260" />;
      case 'picture':
        return <ImageIcon size={20} className="text-purple-400" data-id="element-17261" />;
      case 'video':
        return <Play size={20} className="text-red-400" data-id="element-17262" />;
      default:
        return <Gift size={20} className="text-pink-400" data-id="element-17263" />;
    }
  };
  return <div className="w-full max-w-4xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-sm p-4" data-id="element-17264">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-17265">
        <div className="flex items-center gap-3" data-id="element-17266">
          <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 rounded-full" data-id="element-17267">
            <Gift size={24} className="text-white" data-id="element-17268" />
          </div>
          <div data-id="element-17269">
            <h2 className="text-xl font-bold dark:text-white" data-id="element-17270">
              Lootbox Rewards
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-17271">
              Unlock exclusive rewards and surprises
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-17272">
          <button onClick={() => setShowRewardHistory(!showRewardHistory)} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 text-sm transition-colors" data-id="element-17273">
            <History size={16} data-id="element-17274" />
            <span data-id="element-17275">History</span>
          </button>
          {onClose && <button onClick={onClose} className="flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full text-gray-700 dark:text-gray-300 transition-colors" data-id="element-17276">
              <X size={16} data-id="element-17277" />
            </button>}
        </div>
      </div>
      {/* Membership Tier Indicator */}
      <div className="mb-6 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-17278">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-17279">
          <div className="flex items-center gap-3" data-id="element-17280">
            <div className={`w-10 h-10 flex items-center justify-center rounded-full ${userTier === 'free' ? 'bg-gray-200 dark:bg-gray-700' : userTier === 'silver' ? 'bg-gradient-to-r from-blue-400 to-blue-500' : userTier === 'gold' ? 'bg-gradient-to-r from-yellow-400 to-amber-500' : 'bg-gradient-to-r from-purple-500 to-pink-500'}`} data-id="element-17281">
              {userTier === 'free' ? <Shield size={20} className="text-gray-500 dark:text-gray-400" data-id="element-17282" /> : userTier === 'silver' ? <Award size={20} className="text-white" data-id="element-17283" /> : userTier === 'gold' ? <Crown size={20} className="text-white" data-id="element-17284" /> : <Diamond size={20} className="text-white" data-id="element-17285" />}
            </div>
            <div data-id="element-17286">
              <h3 className="font-medium dark:text-white" data-id="element-17287">
                {userTier.charAt(0).toUpperCase() + userTier.slice(1)} Member
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-17288">
                {userTier === 'free' ? 'Upgrade to unlock premium lootboxes' : userTier === 'silver' ? 'Access to weekly premium lootboxes' : userTier === 'gold' ? 'Enhanced rewards in all lootboxes' : 'Exclusive access to legendary lootboxes'}
              </p>
            </div>
          </div>
          {userTier === 'free' && onUpgrade && <button onClick={onUpgrade} className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-17289">
              Upgrade Now
            </button>}
        </div>
      </div>
      {showRewardHistory ? <div className="animate-in fade-in duration-300" data-id="element-17290">
          <div className="flex items-center justify-between mb-4" data-id="element-17291">
            <h3 className="font-bold dark:text-white" data-id="element-17292">Reward History</h3>
            <button onClick={() => setShowRewardHistory(false)} className="text-sm text-blue-600 dark:text-blue-400" data-id="element-17293">
              Back to Lootboxes
            </button>
          </div>
          <div className="space-y-3 mb-4 max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin" data-id="element-17294">
            {rewardHistory.length > 0 ? rewardHistory.sort((a, b) => b.date.getTime() - a.date.getTime()).map(reward => <div key={reward.id} className={`p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border-l-4 ${getRarityBorder(reward.rarity)}`} data-id="element-17295">
                    <div className="flex items-start gap-3" data-id="element-17296">
                      <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-gradient-to-r ${getRarityColor(reward.rarity)}`} data-id="element-17297">
                        {getRewardIcon(reward.type)}
                      </div>
                      <div className="flex-1" data-id="element-17298">
                        <div className="flex items-start justify-between" data-id="element-17299">
                          <div data-id="element-17300">
                            <h4 className="font-medium dark:text-white" data-id="element-17301">
                              {reward.title}
                            </h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-17302">
                              {reward.description}
                            </p>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-17303">
                            {formatDate(reward.date)}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-1" data-id="element-17304">
                          <div className="flex items-center gap-1" data-id="element-17305">
                            <span className={`text-xs px-1.5 py-0.5 rounded-sm ${getRarityColor(reward.rarity)} ${getRarityText(reward.rarity)}`} data-id="element-17306">
                              {reward.rarity.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-17307">
                              from{' '}
                              {lootboxes.find(lb => lb.id === reward.lootboxId)?.name || 'Lootbox'}
                            </span>
                          </div>
                          {reward.imageUrl && <button className="text-xs text-blue-600 dark:text-blue-400" data-id="element-17308">
                              View Item
                            </button>}
                        </div>
                      </div>
                    </div>
                  </div>) : <div className="text-center py-8" data-id="element-17309">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-17310">
                  <Gift size={24} className="text-gray-400" data-id="element-17311" />
                </div>
                <h4 className="font-medium dark:text-white mb-1" data-id="element-17312">
                  No rewards yet
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-17313">
                  Open lootboxes to earn exciting rewards
                </p>
              </div>}
          </div>
        </div> : <div className="animate-in fade-in duration-300" data-id="element-17314">
          <h3 className="font-bold dark:text-white mb-4" data-id="element-17315">
            Available Lootboxes
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-17316">
            {lootboxes.filter(lb => lb.available || lb.requiresPremium && userTier !== 'free' || lb.minTier === 'platinum' && userTier === 'platinum').map(lootbox => {
          const isLocked = lootbox.requiresPremium && userTier === 'free' || lootbox.minTier === 'silver' && userTier === 'free' || lootbox.minTier === 'gold' && (userTier === 'free' || userTier === 'silver') || lootbox.minTier === 'platinum' && userTier !== 'platinum';
          const onCooldown = countdowns[lootbox.id] !== undefined;
          return <div key={lootbox.id} className={`relative overflow-hidden rounded-xl border ${isLocked ? 'border-gray-300 dark:border-gray-700' : getRarityBorder(lootbox.rarity)} bg-white dark:bg-gray-900`} data-id="element-17317">
                    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${getRarityColor(lootbox.rarity)}`} data-id="element-17318"></div>
                    <div className="p-4" data-id="element-17319">
                      <div className="flex items-start gap-3" data-id="element-17320">
                        <div className={`relative w-16 h-16 flex-shrink-0 flex items-center justify-center ${isLocked ? 'bg-gray-100 dark:bg-gray-800' : `bg-gradient-to-br ${getRarityColor(lootbox.rarity)}`} rounded-lg overflow-hidden`} data-id="element-17321">
                          {/* Lootbox image or icon */}
                          {isLocked ? <Lock size={24} className="text-gray-400 dark:text-gray-600" data-id="element-17322" /> : onCooldown ? <Clock size={24} className={getRarityText(lootbox.rarity)} data-id="element-17323" /> : <>
                              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-30" data-id="element-17324"></div>
                              <Gift size={24} className={getRarityText(lootbox.rarity)} data-id="element-17325" />
                            </>}
                        </div>
                        <div className="flex-1" data-id="element-17326">
                          <div className="flex items-start justify-between" data-id="element-17327">
                            <div data-id="element-17328">
                              <h4 className="font-medium dark:text-white flex items-center gap-1" data-id="element-17329">
                                {lootbox.name}
                                {lootbox.rarity === 'legendary' && <Star size={14} className="text-amber-400 animate-pulse" data-id="element-17330" />}
                              </h4>
                              <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-17331">
                                {lootbox.description}
                              </p>
                            </div>
                            <div className={`px-1.5 py-0.5 rounded-sm text-xs ${getRarityColor(lootbox.rarity)} ${getRarityText(lootbox.rarity)}`} data-id="element-17332">
                              {lootbox.rarity.toUpperCase()}
                            </div>
                          </div>
                          {/* Cooldown timer or status */}
                          <div className="mt-2" data-id="element-17333">
                            {isLocked ? <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-17334">
                                <Lock size={12} data-id="element-17335" />
                                <span data-id="element-17336">
                                  {lootbox.minTier === 'silver' ? 'Silver' : lootbox.minTier === 'gold' ? 'Gold' : 'Platinum'}{' '}
                                  membership required
                                </span>
                              </div> : onCooldown ? <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-17337">
                                <Clock size={12} data-id="element-17338" />
                                <span data-id="element-17339">
                                  Available in: {countdowns[lootbox.id]}
                                </span>
                              </div> : <div className="flex items-center gap-1 text-xs text-green-500 dark:text-green-400" data-id="element-17340">
                                <Unlock size={12} data-id="element-17341" />
                                <span data-id="element-17342">Ready to open!</span>
                              </div>}
                          </div>
                        </div>
                      </div>
                      {/* Potential rewards preview */}
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-17343">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-17344">
                          Potential rewards:
                        </div>
                        <div className="flex flex-wrap gap-1" data-id="element-17345">
                          {lootbox.potentialRewards.map((reward, i) => <div key={i} className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-md text-gray-700 dark:text-gray-300" data-id="element-17346">
                              {reward}
                            </div>)}
                        </div>
                      </div>
                      {/* Action button */}
                      <div className="mt-3" data-id="element-17347">
                        {isLocked ? <button onClick={onUpgrade} className="w-full py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-17348">
                            Upgrade to Unlock
                          </button> : onCooldown ? <button disabled className="w-full py-2 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed" data-id="element-17349">
                            On Cooldown
                          </button> : <button onClick={() => handleOpenLootbox(lootbox)} className={`w-full py-2 bg-gradient-to-r ${lootbox.rarity === 'common' ? 'from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800' : lootbox.rarity === 'rare' ? 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800' : lootbox.rarity === 'epic' ? 'from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800' : 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'} text-white rounded-lg text-sm font-medium transition-colors animate-pulse-subtle`} data-id="element-17350">
                            Open Lootbox
                          </button>}
                      </div>
                    </div>
                  </div>;
        })}
          </div>
          {/* Upcoming Lootboxes */}
          <h3 className="font-bold dark:text-white mb-4" data-id="element-17351">Upcoming Lootboxes</h3>
          <div className="space-y-3 mb-4" data-id="element-17352">
            {lootboxes.filter(lb => !lb.available && countdowns[lb.id] !== undefined && (lb.requiresPremium && userTier !== 'free' || !lb.requiresPremium) && (lb.minTier === 'silver' && userTier !== 'free' || lb.minTier === 'gold' && (userTier === 'gold' || userTier === 'platinum') || lb.minTier === 'platinum' && userTier === 'platinum' || lb.minTier === 'free')).map(lootbox => <div key={lootbox.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-17353">
                  <div className="flex items-center justify-between" data-id="element-17354">
                    <div className="flex items-center gap-3" data-id="element-17355">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r ${getRarityColor(lootbox.rarity)} opacity-50`} data-id="element-17356">
                        <Clock size={20} className={getRarityText(lootbox.rarity)} data-id="element-17357" />
                      </div>
                      <div data-id="element-17358">
                        <h4 className="font-medium dark:text-white" data-id="element-17359">
                          {lootbox.name}
                        </h4>
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-17360">
                          <Calendar size={12} data-id="element-17361" />
                          <span data-id="element-17362">
                            Available in: {countdowns[lootbox.id] || 'Soon'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`px-1.5 py-0.5 rounded-sm text-xs ${getRarityColor(lootbox.rarity)} ${getRarityText(lootbox.rarity)}`} data-id="element-17363">
                      {lootbox.rarity.toUpperCase()}
                    </div>
                  </div>
                </div>)}
            {lootboxes.filter(lb => !lb.available && countdowns[lb.id] !== undefined && (lb.requiresPremium && userTier !== 'free' || !lb.requiresPremium) && (lb.minTier === 'silver' && userTier !== 'free' || lb.minTier === 'gold' && (userTier === 'gold' || userTier === 'platinum') || lb.minTier === 'platinum' && userTier === 'platinum' || lb.minTier === 'free')).length === 0 && <div className="text-center py-6" data-id="element-17364">
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-17365">
                  No upcoming lootboxes at the moment.
                </p>
              </div>}
          </div>
          {/* Premium Upgrade Prompt */}
          {userTier === 'free' && <div className="rounded-xl overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-0.5 mb-4" data-id="element-17366">
              <div className="bg-white dark:bg-gray-900 rounded-[0.65rem] p-4" data-id="element-17367">
                <div className="flex items-start gap-4" data-id="element-17368">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 rounded-full" data-id="element-17369">
                    <Plus size={24} className="text-white" data-id="element-17370" />
                  </div>
                  <div className="flex-1" data-id="element-17371">
                    <h3 className="font-bold dark:text-white mb-1" data-id="element-17372">
                      Upgrade to Premium
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-17373">
                      Get access to premium lootboxes with better rewards and
                      faster cooldowns
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-17374">
                      <button onClick={onUpgrade} className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-17375">
                        Silver Membership
                      </button>
                      <button onClick={onUpgrade} className="px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-17376">
                        Gold Membership
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>}
      {/* Lootbox Opening Modal */}
      {showLootboxOpening && currentReward && <LootboxOpening reward={currentReward} showReward={true} onClose={handleCloseLootboxOpening} data-id="element-17377" />}
    </div>;
};
// Missing Award component used in the code
const Award = ({
  size = 24,
  className = ''
}) => {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-17378">
      <circle cx="12" cy="8" r="6" data-id="element-17379" />
      <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" data-id="element-17380" />
    </svg>;
};